import { useState } from 'react'
import { addReaction, removeReaction } from '../../api'
import { colors } from '../../colors'
import heartFilledIcon from '../../images/heart_filled.svg'
import heartOutlineIcon from '../../images/heart_outline.svg'
import { BargainUserInfo, Post, PostReaction, ReactionType } from '../../types'
import LoginModal from '../LoginModal'

interface PostReactionsProps {
  post: Post
  bargain_user_info: BargainUserInfo
  onReactionChange?: () => void
  /** 表示バリアント: default=複数ボタン表示, badge=バッジ表示(heartのみ) */
  variant?: 'default' | 'badge'
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '8px',
  },
  reactionButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '8px',
    WebkitTapHighlightColor: 'transparent',
  },
  reactionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    borderRadius: '20px',
    color: colors['text-secondary'],
    fontSize: '12px',
    lineHeight: '1.5',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  reactionCount: {
    margin: '0 4px 0 2px',
    fontWeight: 'bold',
  },
  badge: {
    backgroundColor: '#000000BD',
    borderRadius: '16px',
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    color: colors.white,
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
  },
}

const PostReactions: React.FC<PostReactionsProps> = ({
  post,
  bargain_user_info,
  onReactionChange,
  variant = 'default',
}) => {
  const [reactions, setReactions] = useState<PostReaction[]>(post.reactions || [])
  const [userReactions, setUserReactions] = useState<PostReaction[]>(post.user_reactions || [])
  const [showLoginModal, setShowLoginModal] = useState(false)

  const reactionTypes = post.reaction_types || []
  const themeReactionTypes = post.theme_reaction_types || []

  const handleAddReaction = async (reactionTypeId: number) => {
    try {
      // 対象のリアクションタイプとテーマリアクションタイプを取得
      const reactionType = reactionTypes.find((type) => type.id === reactionTypeId)
      const themeReactionType = themeReactionTypes.find(
        (themeReaction) => themeReaction.bargain_message_board_reaction_type_id === reactionTypeId
      )

      if (!reactionType || !themeReactionType || !bargain_user_info || !bargain_user_info.type) {
        return
      }

      // 1. 楽観的に状態を更新（正しいtheme_reaction_type_idを使用）
      const optimisticReaction: PostReaction = {
        id: -1, // 一時的なID（サーバーからの応答で上書きされる）
        bargain_message_board_post_id: post.id,
        bargain_message_board_theme_reaction_type_id: themeReactionType.id,
        reactable_id: bargain_user_info.id,
        reactable_type: bargain_user_info.type,
        reaction_type: reactionType,
      }

      // ユーザーリアクションに追加
      setUserReactions((prev) => [...prev, optimisticReaction])

      // 全リアクションに追加
      setReactions((prev) => [...prev, optimisticReaction])

      // 2. バックグラウンドでAPIリクエスト
      const response = await addReaction(post.id, reactionTypeId)

      // 3. 成功したら一時IDを実際のIDで更新
      const themeReactionTypeId = themeReactionType.id
      setUserReactions((prev) =>
        prev.map((r) =>
          r.id === -1 && r.bargain_message_board_theme_reaction_type_id === themeReactionTypeId
            ? response
            : r
        )
      )

      setReactions((prev) =>
        prev.map((r) =>
          r.id === -1 && r.bargain_message_board_theme_reaction_type_id === themeReactionTypeId
            ? response
            : r
        )
      )

      if (onReactionChange) {
        onReactionChange()
      }
    } catch {
      // 4. エラー時は楽観的に追加したリアクションを削除（ロールバック）
      const themeReactionType = themeReactionTypes.find(
        (themeReaction) => themeReaction.bargain_message_board_reaction_type_id === reactionTypeId
      )

      if (themeReactionType) {
        setUserReactions((prev) =>
          prev.filter(
            (r) =>
              !(
                r.id === -1 &&
                r.bargain_message_board_theme_reaction_type_id === themeReactionType.id
              )
          )
        )

        setReactions((prev) =>
          prev.filter(
            (r) =>
              !(
                r.id === -1 &&
                r.bargain_message_board_theme_reaction_type_id === themeReactionType.id
              )
          )
        )
      }
    }
  }

  // リアクションの削除（楽観的UI更新）
  const handleRemoveReaction = async (reactionId: number) => {
    // 削除するリアクションを記憶
    const reactionToRemove = userReactions.find((r) => r.id === reactionId)

    if (!reactionToRemove || reactionToRemove.id === -1) return

    try {
      // 1. 楽観的に状態を更新
      setUserReactions((prev) => prev.filter((r) => r.id !== reactionId))
      setReactions((prev) => prev.filter((r) => r.id !== reactionId))

      // 2. バックグラウンドでAPIリクエスト
      await removeReaction(post.id, reactionId)

      if (onReactionChange) {
        onReactionChange()
      }
    } catch {
      // 3. エラー時は削除したリアクションを復元（ロールバック）
      setUserReactions((prev) => [...prev, reactionToRemove])
      setReactions((prev) => [...prev, reactionToRemove])
    }
  }

  // ユーザーが特定のリアクションタイプに既にリアクションしているか確認
  const hasUserReacted = (reactionTypeId: number): PostReaction | undefined => {
    const themeReactionType = themeReactionTypes.find(
      (themeReaction) => themeReaction.bargain_message_board_reaction_type_id === reactionTypeId
    )
    if (!themeReactionType) return undefined
    return userReactions.find(
      (reaction) => reaction.bargain_message_board_theme_reaction_type_id === themeReactionType.id
    )
  }

  // リアクションをタイプごとにグループ化
  const groupedReactions = reactions.reduce<
    Record<number, { count: number; type: ReactionType | undefined }>
  >((acc, reaction) => {
    const themeReactionType = themeReactionTypes.find(
      (themeReaction) => themeReaction.id === reaction.bargain_message_board_theme_reaction_type_id
    )
    if (!themeReactionType) return acc

    const reactionType = reactionTypes.find(
      (type) => type.id === themeReactionType.bargain_message_board_reaction_type_id
    )

    if (!reactionType) return acc

    if (!acc[reactionType.id]) {
      acc[reactionType.id] = { count: 0, type: reactionType }
    }
    acc[reactionType.id].count += 1
    return acc
  }, {})

  const formatReactionCount = (count: number): string => {
    if (count === 0) return ''
    if (count > 999) return '999+'
    return count.toString()
  }

  const isGuest = bargain_user_info.type === 'guest'

  // バッジ表示（heartリアクションのみ）
  if (variant === 'badge') {
    const heartReactionType = reactionTypes.find((type) => type.code === 'heart')
    if (!heartReactionType) return null

    const userReaction = hasUserReacted(heartReactionType.id)
    const isUserReacted = !!userReaction
    const count = groupedReactions[heartReactionType.id]?.count || 0

    return (
      <>
        <button
          style={styles.badge}
          onClick={(e) => {
            e.stopPropagation()
            if (isGuest) {
              setShowLoginModal(true)
              return
            }
            if (isUserReacted && userReaction) {
              void handleRemoveReaction(userReaction.id)
            } else {
              void handleAddReaction(heartReactionType.id)
            }
          }}
        >
          <img
            src={isUserReacted ? heartFilledIcon : heartOutlineIcon}
            alt="食べたい"
            width={16}
            height={16}
          />
          <span>{count}</span>
        </button>
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </>
    )
  }

  // デフォルト表示（複数ボタン）
  return (
    <div style={styles.container}>
      <div style={styles.reactionButtons}>
        {reactionTypes.map((reactionType) => {
          const userReaction = hasUserReacted(reactionType.id)
          const isUserReacted = !!userReaction
          const count = groupedReactions[reactionType.id]?.count || 0

          // ボタンのスタイル
          const buttonStyle: React.CSSProperties = {
            ...styles.reactionButton,
            ...(isUserReacted
              ? {
                backgroundColor: reactionType.highlight_color,
                border: `1px solid ${reactionType.border_color}`,
              }
            : {
              backgroundColor: colors['white'],
              border: `1px solid ${colors['border-light']}`,
            }),
          }

          return (
            <button
              key={reactionType.id}
              style={{
                ...buttonStyle,
                ...(isGuest ? { cursor: 'default', opacity: 1 } : {}),
              }}
              onClick={() => {
                // ゲストユーザーの場合はログインモーダルを表示
                if (isGuest) {
                  setShowLoginModal(true)
                  return
                }

                if (isUserReacted && userReaction) {
                  void handleRemoveReaction(userReaction.id)
                } else {
                  void handleAddReaction(reactionType.id)
                }
              }}
            >
              <img
                src={`${count > 0 ? reactionType.colored_icon_url : reactionType.grayed_icon_url}`}
                alt="reaction icon"
                width={16}
              />
              <span style={styles.reactionCount}>{formatReactionCount(count)}</span>
              {reactionType.display_name}
            </button>
          )
        })}
      </div>

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  )
}

export default PostReactions
